import { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { BLOCK_DEFS, getProp, newBlock } from './blockSchema.js';
import { useEditor } from './editorStore.js';
import { useAuth } from '../contexts/AuthContext.jsx';
import { mediaApi } from '../api/media.js';
import { apiError } from '../api/client.js';
import MediaLibraryModal from './MediaLibraryModal.jsx';

export default function Inspector() {
  const block = useEditor((s) => {
    const id = s.selectedId;
    if (!id) return null;
    return findBlock(s.blocks, id);
  });
  const updateProp = useEditor((s) => s.updateBlockProps);
  const removeBlock = useEditor((s) => s.removeBlock);
  const select = useEditor((s) => s.select);

  if (!block) {
    return (
      <aside className="w-72 shrink-0 border-l border-ink-200 bg-white p-5 text-sm text-ink-500 dark:border-ink-800 dark:bg-ink-900">
        <div className="font-semibold text-ink-700 dark:text-ink-200">Inspector</div>
        <p className="mt-2 text-xs">Select a block on the canvas to edit its properties.</p>
      </aside>
    );
  }

  const def = BLOCK_DEFS[block.type];

  return (
    <aside className="scroll-pane w-72 shrink-0 overflow-y-auto border-l border-ink-200 bg-white p-5 dark:border-ink-800 dark:bg-ink-900">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-ink-500">{def.label}</div>
          <div className="font-mono text-[11px] text-ink-400">{block.id.slice(0, 8)}</div>
        </div>
        <button
          onClick={() => {
            removeBlock(block.id);
            select(null);
          }}
          className="btn-ghost h-8 px-2 text-xs text-red-500 hover:bg-red-50"
        >
          Delete
        </button>
      </div>

      <div className="space-y-4">
        {def.fields.map((field) => (
          <Field
            key={field.key}
            field={field}
            block={block}
            value={getProp(block, field.key)}
            onChange={(v) => updateProp(block.id, field.key, v)}
          />
        ))}
      </div>
    </aside>
  );
}

function Field({ field, value, onChange, block }) {
  const { type, label, options } = field;

  switch (type) {
    case 'textarea':
      return (
        <Wrap label={label}>
          <textarea
            className="input min-h-[80px]"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
          />
        </Wrap>
      );

    case 'select':
      return (
        <Wrap label={label}>
          <select
            className="input"
            value={value ?? ''}
            onChange={(e) => {
              const raw = e.target.value;
              const opt = options.find((o) => String(o.value) === raw);
              onChange(opt ? opt.value : raw);
            }}
          >
            {options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Wrap>
      );

    case 'color':
      return (
        <Wrap label={label}>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={value || '#ffffff'}
              onChange={(e) => onChange(e.target.value)}
              className="h-9 w-12 cursor-pointer rounded border border-ink-200"
            />
            <input
              className="input flex-1 font-mono text-xs"
              value={value ?? ''}
              onChange={(e) => onChange(e.target.value)}
            />
          </div>
        </Wrap>
      );

    case 'number':
      return (
        <Wrap label={label}>
          <input
            type="number"
            className="input"
            value={value ?? 0}
            onChange={(e) => onChange(Number(e.target.value))}
          />
        </Wrap>
      );

    case 'pairs':
      return <PairsField label={label} value={value} onChange={onChange} />;

    case 'qaList':
      return <QaListField label={label} value={value} onChange={onChange} />;

    case 'footerGroups':
      return <FooterGroupsField label={label} value={value} onChange={onChange} />;

    case 'columnControls':
      return <ColumnControlsField label={label} block={block} />;

    case 'imageUrl':
      return <ImageUrlField label={label} value={value} onChange={onChange} />;

    default:
      return (
        <Wrap label={label}>
          <input
            className="input"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
          />
        </Wrap>
      );
  }
}

function Wrap({ label, children }) {
  return (
    <div>
      <label className="label">{label}</label>
      {children}
    </div>
  );
}

// ── list editors ─────────────────────────────────────────────────────────

// Edits an array of {label, href}. Renders as a small repeater.
function PairsField({ label, value, onChange }) {
  const items = Array.isArray(value) ? value : [];

  function update(i, patch) {
    const next = items.slice();
    next[i] = { ...next[i], ...patch };
    onChange(next);
  }
  function remove(i) {
    const next = items.slice();
    next.splice(i, 1);
    onChange(next);
  }
  function add() {
    onChange([...items, { label: 'New link', href: '#' }]);
  }

  return (
    <div>
      <label className="label">{label}</label>
      <ul className="space-y-2">
        {items.map((item, i) => (
          <li key={i} className="rounded-lg border border-ink-200 p-2 dark:border-ink-800">
            <input
              className="input mb-1 text-sm"
              placeholder="Label"
              value={item.label || ''}
              onChange={(e) => update(i, { label: e.target.value })}
            />
            <div className="flex items-center gap-1">
              <input
                className="input text-xs"
                placeholder="https://…"
                value={item.href || ''}
                onChange={(e) => update(i, { href: e.target.value })}
              />
              <button
                type="button"
                onClick={() => remove(i)}
                className="btn-ghost h-9 px-2 text-xs text-red-500"
                title="Remove"
              >
                ×
              </button>
            </div>
          </li>
        ))}
      </ul>
      <button type="button" onClick={add} className="btn-ghost mt-2 w-full justify-center text-xs">
        + Add link
      </button>
    </div>
  );
}

// Edits an array of {question, answer} for accordion blocks.
function QaListField({ label, value, onChange }) {
  const items = Array.isArray(value) ? value : [];

  function update(i, patch) {
    const next = items.slice();
    next[i] = { ...next[i], ...patch };
    onChange(next);
  }
  function remove(i) {
    const next = items.slice();
    next.splice(i, 1);
    onChange(next);
  }
  function add() {
    onChange([...items, { question: 'New question', answer: 'Answer goes here.' }]);
  }

  return (
    <div>
      <label className="label">{label}</label>
      <ul className="space-y-2">
        {items.map((item, i) => (
          <li key={i} className="rounded-lg border border-ink-200 p-2 dark:border-ink-800">
            <input
              className="input mb-1 text-sm"
              placeholder="Question"
              value={item.question || ''}
              onChange={(e) => update(i, { question: e.target.value })}
            />
            <textarea
              className="input min-h-[60px] text-xs"
              placeholder="Answer"
              value={item.answer || ''}
              onChange={(e) => update(i, { answer: e.target.value })}
            />
            <div className="mt-1 flex justify-end">
              <button
                type="button"
                onClick={() => remove(i)}
                className="btn-ghost h-7 px-2 text-xs text-red-500"
              >
                Remove
              </button>
            </div>
          </li>
        ))}
      </ul>
      <button type="button" onClick={add} className="btn-ghost mt-2 w-full justify-center text-xs">
        + Add Q & A
      </button>
    </div>
  );
}

// Edits an array of {heading, links: [{label, href}]} for the footer block.
function FooterGroupsField({ label, value, onChange }) {
  const groups = Array.isArray(value) ? value : [];

  function updateGroup(i, patch) {
    const next = groups.slice();
    next[i] = { ...next[i], ...patch };
    onChange(next);
  }
  function removeGroup(i) {
    const next = groups.slice();
    next.splice(i, 1);
    onChange(next);
  }
  function addGroup() {
    onChange([...groups, { heading: 'New section', links: [] }]);
  }

  return (
    <div>
      <label className="label">{label}</label>
      <ul className="space-y-3">
        {groups.map((g, i) => (
          <li key={i} className="rounded-lg border border-ink-200 p-2 dark:border-ink-800">
            <input
              className="input mb-2 text-sm"
              placeholder="Group heading"
              value={g.heading || ''}
              onChange={(e) => updateGroup(i, { heading: e.target.value })}
            />
            <PairsField
              label="Links"
              value={g.links || []}
              onChange={(links) => updateGroup(i, { links })}
            />
            <div className="mt-1 flex justify-end">
              <button
                type="button"
                onClick={() => removeGroup(i)}
                className="btn-ghost h-7 px-2 text-xs text-red-500"
              >
                Remove group
              </button>
            </div>
          </li>
        ))}
      </ul>
      <button type="button" onClick={addGroup} className="btn-ghost mt-2 w-full justify-center text-xs">
        + Add group
      </button>
    </div>
  );
}

// URL input + upload-from-disk + media library picker. The library picker
// opens a modal with every image previously uploaded to this workspace so
// users can reuse assets without re-uploading.
function ImageUrlField({ label, value, onChange }) {
  const { workspace } = useAuth();
  const fileInputRef = useRef(null);
  const [progress, setProgress] = useState(null); // 0..100 or null
  const [libraryOpen, setLibraryOpen] = useState(false);

  function pickFile() {
    fileInputRef.current?.click();
  }

  async function onPick(e) {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file later
    if (!file || !workspace) return;
    setProgress(0);
    try {
      const media = await mediaApi.upload(workspace.id, file, setProgress);
      onChange(media.url);
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setProgress(null);
    }
  }

  return (
    <div>
      <label className="label">{label}</label>
      {value && (
        <div className="mb-2 overflow-hidden rounded-lg border border-ink-200 bg-ink-50 dark:border-ink-800 dark:bg-ink-950">
          <img
            src={value}
            alt=""
            className="block max-h-32 w-full object-contain"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        </div>
      )}
      <div className="flex items-center gap-1">
        <input
          className="input flex-1 text-xs"
          placeholder="https://… or upload"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
        />
        <button
          type="button"
          onClick={pickFile}
          className="btn-secondary h-9 px-2 text-xs"
          disabled={progress !== null}
          title="Upload from your computer"
        >
          {progress !== null ? `${progress}%` : '↑'}
        </button>
        <button
          type="button"
          onClick={() => setLibraryOpen(true)}
          className="btn-secondary h-9 px-2 text-xs"
          title="Pick from media library"
        >
          ▦
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={onPick}
        />
      </div>
      {value && (
        <button
          type="button"
          onClick={() => onChange('')}
          className="btn-ghost mt-1 text-xs text-ink-500"
        >
          Clear image
        </button>
      )}
      <MediaLibraryModal
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        onSelect={(url) => onChange(url)}
        currentUrl={value}
      />
    </div>
  );
}

// Add / remove column buckets inside a Columns block. Each click commits a
// single tree mutation (so undo rewinds one column at a time).
function ColumnControlsField({ label, block }) {
  const insertBlock = useEditor((s) => s.insertBlock);
  const removeBlock = useEditor((s) => s.removeBlock);
  const columns = (block.children || []).filter((c) => c.type === 'column');
  const count = columns.length;

  function add() {
    if (count >= 6) {
      toast.error('Max 6 columns');
      return;
    }
    insertBlock(newBlock('column'), block.id);
  }

  function removeLast() {
    if (count <= 1) {
      toast.error('Need at least 1 column');
      return;
    }
    const last = columns[columns.length - 1];
    if ((last.children || []).length > 0) {
      const ok = window.confirm('This column has content. Remove it anyway?');
      if (!ok) return;
    }
    removeBlock(last.id);
  }

  return (
    <div>
      <label className="label">{label}</label>
      <div className="flex items-center gap-2">
        <span className="rounded-md bg-ink-100 px-3 py-1.5 text-sm font-semibold dark:bg-ink-800">
          {count}
        </span>
        <button type="button" onClick={add} className="btn-secondary flex-1 text-xs">
          + Add column
        </button>
        <button type="button" onClick={removeLast} className="btn-ghost text-xs text-red-500">
          − Remove
        </button>
      </div>
    </div>
  );
}

function findBlock(blocks, id) {
  for (const b of blocks) {
    if (b.id === id) return b;
    if (b.children) {
      const f = findBlock(b.children, id);
      if (f) return f;
    }
  }
  return null;
}
